import React from "react";
import { ShimmerBlock } from "../../common/Skeleton";

/* ──────────────────────────────────────────────────────────────────────────
 * Per-section skeletons — can be composed individually inside pages that
 * want to keep the real header / tabs visible while content is loading.
 * ────────────────────────────────────────────────────────────────────────── */

/** Skeleton for the sticky page header (title shimmer). */
export const SeparationHeaderSkeleton: React.FC = () => (
  <div className="sticky top-0 z-30 bg-surface-muted px-4 sm:px-6 pt-6 mb-6 animate-pulse">
    <ShimmerBlock className="h-8 md:h-10 w-48 md:w-64 rounded-lg" />
  </div>
);

/** Skeleton for a form card (two inputs + textarea + submit button). */
export const SeparationFormSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl shadow-soft border border-slate-100 p-6 w-full animate-pulse">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      <div>
        <ShimmerBlock className="h-4 w-32 mb-2 rounded" />
        <ShimmerBlock className="h-10 w-full rounded-lg" />
      </div>
      <div>
        <ShimmerBlock className="h-4 w-32 mb-2 rounded" />
        <ShimmerBlock className="h-10 w-full rounded-lg" />
      </div>
    </div>
    <div className="mb-4">
      <ShimmerBlock className="h-4 w-40 mb-2 rounded" />
      <ShimmerBlock className="h-20 w-full rounded-lg" />
    </div>
    <div className="flex justify-end">
      <ShimmerBlock className="h-10 w-24 rounded-lg" />
    </div>
  </div>
);

/** Skeleton for a table card (header row + 8 data rows). */
export const SeparationTableSkeleton: React.FC<{ rows?: number }> = ({
  rows = 8,
}) => (
  <div className="bg-white rounded-xl shadow-soft border border-slate-100 overflow-hidden w-full animate-pulse">
    {/* Card Header */}
    <div className="px-6 py-5 border-b border-slate-100 flex justify-between items-center">
      <ShimmerBlock className="h-6 w-40 rounded-md" />
      <ShimmerBlock className="h-8 w-24 rounded-md hidden sm:block" />
    </div>

    {/* Card Body (Rows) */}
    <div className="p-0">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex flex-wrap sm:flex-nowrap items-center gap-4 px-6 py-4 border-b border-slate-50 last:border-0"
        >
          <div className="w-full sm:w-1/3 space-y-2">
            <ShimmerBlock className="h-4 w-3/4 rounded" />
            <ShimmerBlock className="h-3 w-1/2 rounded" />
          </div>
          <div className="w-full sm:w-1/4 hidden sm:block">
            <ShimmerBlock className="h-4 w-2/3 rounded" />
          </div>
          <div className="w-full sm:w-1/4 hidden sm:block">
            <ShimmerBlock className="h-6 w-20 rounded-full" />
          </div>
          <div className="w-full sm:w-auto flex-1 flex justify-end gap-2">
            <ShimmerBlock className="h-8 w-8 rounded-md" />
            <ShimmerBlock className="h-8 w-8 rounded-md" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

/** Skeleton for top-level filter / action bar (no form). */
export const SeparationFiltersSkeleton: React.FC = () => (
  <div className="flex flex-col sm:flex-row gap-4 mb-2 animate-pulse">
    <ShimmerBlock className="h-10 w-full sm:w-64 rounded-lg" />
    <ShimmerBlock className="h-10 w-full sm:w-48 rounded-lg" />
  </div>
);

/** Skeleton for an exit interview card (title + 3 question blocks + buttons). */
export const SeparationInterviewSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl shadow-soft border border-slate-100 p-6 w-full animate-pulse">
    <ShimmerBlock className="h-6 w-40 mb-6 rounded-md" />
    <div className="space-y-6">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i}>
          <ShimmerBlock className="h-4 w-1/3 mb-2 rounded" />
          <ShimmerBlock className="h-10 w-full rounded-lg" />
        </div>
      ))}
    </div>
    <div className="flex gap-3 mt-6">
      <ShimmerBlock className="h-10 w-24 rounded-lg" />
      <ShimmerBlock className="h-10 w-32 rounded-lg" />
    </div>
  </div>
);

/** Skeleton for a My-Exit details card. */
export const SeparationMyExitSkeleton: React.FC = () => (
  <div className="bg-white rounded-xl shadow-soft border border-slate-100 overflow-hidden w-full flex-1 p-6 animate-pulse">
    <div className="flex items-center gap-4 mb-8">
      <ShimmerBlock className="h-6 w-24 rounded" />
      <ShimmerBlock className="h-8 w-32 rounded-full" />
    </div>

    <div className="space-y-6">
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="flex flex-col gap-2 border-b border-slate-50 pb-4 last:border-0"
        >
          <ShimmerBlock className="h-4 w-1/4 rounded" />
          <ShimmerBlock className="h-4 w-1/2 rounded" />
        </div>
      ))}
    </div>
  </div>
);

/* ──────────────────────────────────────────────────────────────────────────
 * Full-page composed skeleton — used by pages that want a single loading
 * placeholder that covers the whole viewport (header + content).
 * ────────────────────────────────────────────────────────────────────────── */

export interface SeparationPageSkeletonProps {
  /** If true, renders a mock form card at the top (useful for Resign/Terminate pages) */
  withForm?: boolean;
  /** If true, renders an interview card skeleton below the table (useful for Lifecycle page) */
  withInterview?: boolean;
  /** If true, renders a single details card layout (useful for My Exit page) */
  myExitLayout?: boolean;
}

export const SeparationPageSkeleton: React.FC<SeparationPageSkeletonProps> = ({
  withForm,
  withInterview,
  myExitLayout,
}) => {
  return (
    <div className="w-full min-h-[90vh] bg-surface-muted flex flex-col">
      {/* Sticky Header Skeleton */}
      <SeparationHeaderSkeleton />

      <div className="px-4 sm:px-6 pt-0 pb-8 max-w-full mx-auto w-full min-w-0 flex flex-col gap-6 flex-1">
        {myExitLayout ? (
          // --- MY EXIT LAYOUT ---
          <SeparationMyExitSkeleton />
        ) : (
          // --- DEFAULT / FORM + TABLE LAYOUT ---
          <>
            {withForm ? (
              <SeparationFormSkeleton />
            ) : (
              <SeparationFiltersSkeleton />
            )}
            <SeparationTableSkeleton />
            {withInterview && <SeparationInterviewSkeleton />}
          </>
        )}
      </div>
    </div>
  );
};
